import { useCallback, useEffect, useRef } from "react";
import { observer } from "mobx-react";

import useNavigationBlocker from "../../../hooks/navigation-blocker-hook";
import useDashboardTranslation from "../../../dashboard-translation/hooks/dashboard-translation-hook";
import useTranslation from "../../../translation/hooks/translation-hook";
import useValidationGroup, { IValidationGroup } from "../../../hooks/validation-group/validation-group-hook";
import TranslationScope, { ITranslationScopeContext } from "../../../translation/translation-scope";
import TranslationTextarea from "../../../translation/translation-textarea/translation-textarea";
import TranslationInput from "../../../translation/translation-input/translation-input";
import { FormFieldLine } from "../../../components/form-field-line/form-field-line";
import { Toggle } from "../../../components/inputs/toggle/toggle";
import Card from "../../../components/layouts/card/card";
import EditorPageLayout from "../../../components/layouts/editor-page/editor-page-layout";
import { SelectDropdown } from "../../../components/inputs/select-dropdown/select-dropdown";
import { IOption } from "../../../components/inputs/select-dropdown-grouped/select-dropdown-grouped";
import { COVERAGE_FEES_MANAGMENT_OPTIONS, COVERAGE_SELECTION_METHOD_OPTIONS } from "../../../services/api/coverage-service";
import ControlGroupWithPreview from "../../../components/layouts/control-group-with-preview/control-group-with-preview";
import ListLayout from "../../../components/layouts/list-layout/list-layout";
import FontBody1 from "../../../components/fonts/font-body1/font-body1";
import { FONT_COLOR, FONT_VARIANT } from "../../../components/fonts/font-props";
import FontBody2 from "../../../components/fonts/font-body2/font-body2";
import FontHeading2 from "../../../components/fonts/font-heading2/font-heading2";
import coverage_description from "../../../images/previews/coverage/coverage_description.png";
import coverage_description_toggle from "../../../images/previews/coverage/coverage_description_toggle.png";
import coverage_description_checkbox from "../../../images/previews/coverage/coverage_description_checkbox.png";
import coverage_portal from "../../../images/previews/coverage/coverage_portal.png";
import coverage_settings_banner from "../../../images/previews/coverage/coverage_settings_banner.png";

import coverageSettingsStore from "../stores/coverage-settings-store";
import ReturnGuardDescriptionVariables from "./components/return-guard-description-variables";
import "./coverage-settings-enabled-page.scss";

const COVERAGE_DESCRIPTION_PREVIEW = {
    toggle: coverage_description_toggle,
    checkbox: coverage_description_checkbox,
};

const CoverageSettingsEnabledPage: React.FC = observer(() => {

    const {
        hasChanges: hasTranslationChanges,
        save: saveTranslations,
        selectedLocale,
        isSaving: isSavingTranslations,
    } = useTranslation();

    const { getTranslation } = useDashboardTranslation();
    const scope = 'dashboard-coverage-settings';
    const vGroup = useValidationGroup();

    const feesManagementDDOptions = (): IOption[] => [
        {
            label: getTranslation('FEES_SELECTOR_OPTION_WAIVE_ALL', scope),
            value: COVERAGE_FEES_MANAGMENT_OPTIONS.WAIVE_ALL,
        },
        {
            label: getTranslation('FEES_SELECTOR_OPTION_WAIVE_LABEL_ONLY', scope),
            value: COVERAGE_FEES_MANAGMENT_OPTIONS.WAIVE_LABEL_ONLY,
        }
    ];

    const coverageSelectionMethodDDOptions = (): IOption[] => [
        {
            label: getTranslation('COVERAGE_SELECTION_METHOD_OPTION_TOGGLE', scope),
            value: COVERAGE_SELECTION_METHOD_OPTIONS.TOGGLE,
        },
        {
            label: getTranslation('COVERAGE_SELECTION_METHOD_OPTION_CHECKBOX', scope),
            value: COVERAGE_SELECTION_METHOD_OPTIONS.CHECKBOX,
        }
    ];

    const selectedFeesManagementOption  = (): IOption|undefined => {
        return feesManagementDDOptions().find((option) => option.value === coverageSettingsStore.feesManagementSelectedOption);
    };

    const selectedCoverageSelectionMethodOption  = (): IOption|undefined => {
        return coverageSelectionMethodDDOptions().find((option) => option.value === coverageSettingsStore.coverageSelectionMethodOption);
    };
    
    const hasChanges = (): boolean => {
        return coverageSettingsStore.hasChanges() || hasTranslationChanges() || storefrontScopeHasChanges();
    }

    const _refStorefrontScope = useRef<IStorefrontScope>();

    const setStorefrontScopeMethods = useCallback((scope: IStorefrontScope) => {
        _refStorefrontScope.current = scope;
    }, []);

    const storefrontScopeHasChanges = (): boolean => {
        if (_refStorefrontScope.current) {
            return _refStorefrontScope.current?.hasChanges();
        }
        return false;
    }

    const storefrontScopeSave = (): void => {
        if (_refStorefrontScope.current) {
            _refStorefrontScope.current?.save();
        }
    }

    const { unblockNavigation } = useNavigationBlocker(
        () => hasChanges(),
    );

    const handleSave = () => {
        const promised = [];
        if (storefrontScopeHasChanges()) {
            promised.push(
                storefrontScopeSave()
            );
        }
        if (hasTranslationChanges()) {
            promised.push(
                saveTranslations()
            );
        }
        if (coverageSettingsStore.hasChanges()) {
            promised.push(
                coverageSettingsStore.save()
            );
        }

        Promise.all(promised).then(() => {
            unblockNavigation();
        })
    }

    return (
        <>
            {coverageSettingsStore.isSellingCoverage ? (
                <div className="coverage-settings-enabled-page-banner">
                    <div className="coverage-settings-enabled-page-banner-content">
                        <FontHeading2 color={FONT_COLOR.PRIMARY2} variant={FONT_VARIANT.BOLD}>{getTranslation('SETTINGS_PAGE_BANNER_TITLE', scope)}</FontHeading2>
                        <FontBody1 color={FONT_COLOR.PRIMARY2}>{getTranslation('SETTINGS_PAGE_BANNER_SUBTITLE', scope)}</FontBody1>
                    </div>
                    <div className="coverage-settings-enabled-page-banner-image">
                        <img src={coverage_settings_banner} alt="Coverage banner" />
                    </div>
                </div>
            ) : null}

            <EditorPageLayout
                toolbar={{
                    saveButton: {
                        hasChanges: hasChanges(),
                        onClick: handleSave,
                        disabled: !vGroup.isValid() || !hasChanges() || coverageSettingsStore.isSaving() || coverageSettingsStore.isLoading || isSavingTranslations(),
                        isValid: vGroup.isValid(),
                    },
                    isLoading: coverageSettingsStore.isSaving(),
                }}
            >
                <ListLayout>
                    <Card
                        isExpandable={true}
                        isExpanded={true}
                        toolbar={ {title: getTranslation('SETTINGS', scope)}}
                        isLoading={coverageSettingsStore.isLoading}
                    >
                        <FormFieldLine label={getTranslation('ENABLE_TOGGLE_TEXT', scope)} note={getTranslation('ENABLE_TOGGLE_DESCRIPTION', scope)}>
                            <Toggle
                                label=""
                                checked={coverageSettingsStore.isSellingCoverage}
                                onChange={v => coverageSettingsStore.setIsSellingCoverage(v)}
                            />
                        </FormFieldLine>
                        <FormFieldLine label={getTranslation('FEES_SELECTOR_TEXT', scope)} note={getTranslation('FEES_SELECTOR_DESCRIPTION', scope)}>
                            <SelectDropdown
                                options={feesManagementDDOptions()}
                                value={selectedFeesManagementOption()}
                                onChange={(v) => {coverageSettingsStore.setFeesManagementSelectedOption(v?.value as COVERAGE_FEES_MANAGMENT_OPTIONS)}}
                                isSearchable={false}
                            />
                        </FormFieldLine>
                    </Card>

                    <Card
                        isExpandable={true}
                        isExpanded={true}
                        toolbar={ {title: getTranslation('CONTENT', scope)}}
                        isLoading={coverageSettingsStore.isLoading}
                    >
                        <ControlGroupWithPreview
                            hasTopBorder={false}
                            gridStyle={{childrenWidth: {width: 1}, previewWidth: {width: 1}}}
                            preview={<img src={coverage_description} alt=""/>}
                        >
                            <div className="edit-translation-field">
                                <FontBody1 color={FONT_COLOR.PRIMARY2}>{getTranslation('EDIT_DESCRIPTION_FIELD_TITLE', scope)}</FontBody1>
                                <FontBody2 color={FONT_COLOR.NEUTRAL3}>{getTranslation('EDIT_DESCRIPTION_FIELD_DESCRIPTION', scope)}</FontBody2>
                                <TranslationScope scope="storefront" locale={selectedLocale}>
                                    <CoverageContentField
                                        required
                                        validationGroup={vGroup}
                                        field="COVERAGE_DESCRIPTION"
                                        onTranslationMethodsReady={setStorefrontScopeMethods}
                                    />
                                </TranslationScope>
                                <ReturnGuardDescriptionVariables scope={scope} />
                            </div>
                        </ControlGroupWithPreview>

                        <ControlGroupWithPreview
                            hasTopBorder={false}
                            gridStyle={{childrenWidth: {width: 1}, previewWidth: {width: 1}}}
                            preview={<img src={COVERAGE_DESCRIPTION_PREVIEW[coverageSettingsStore?.coverageSelectionMethodOption]} alt=""/>}
                        >
                            <div className="edit-translation-field">
                                <FontBody1 color={FONT_COLOR.PRIMARY2}>{getTranslation('EDIT_PORTAL_SELECTION_METHOD_FIELD_TITLE', scope)}</FontBody1>
                                <FontBody2 color={FONT_COLOR.NEUTRAL3}>{getTranslation('EDIT_PORTAL_SELECTION_METHOD_FIELD_DESCRIPTION', scope)}</FontBody2>
                                <SelectDropdown
                                    options={coverageSelectionMethodDDOptions()}
                                    value={selectedCoverageSelectionMethodOption()}
                                    onChange={(v) => {coverageSettingsStore.setCoverageSelectionMethodOption(v?.value as COVERAGE_SELECTION_METHOD_OPTIONS)}}
                                    isSearchable={false}
                                />
                            </div>
                        </ControlGroupWithPreview>

                        <ControlGroupWithPreview
                            hasTopBorder={false}
                            gridStyle={{childrenWidth: {width: 1}, previewWidth: {width: 1}}}
                        >
                            <div className="edit-translation-field">
                                <FontBody1 color={FONT_COLOR.PRIMARY2}>{getTranslation('EDIT_LEARN_MORE_TITLE_FIELD_TITLE', scope)}</FontBody1>
                                <TranslationScope scope="storefront" locale={selectedLocale}>
                                    <CoverageContentField
                                        required
                                        validationGroup={vGroup}
                                        field="COVERAGE_LEARN_MORE_TITLE"
                                        onTranslationMethodsReady={setStorefrontScopeMethods}
                                    />
                                </TranslationScope>
                            </div>

                            <div className="edit-translation-field">
                                <FontBody1 color={FONT_COLOR.PRIMARY2}>{getTranslation('EDIT_LEARN_MORE_CONTENT_FIELD_TITLE', scope)}</FontBody1>
                                <TranslationScope scope="storefront" locale={selectedLocale}>
                                    <CoverageContentField
                                        required
                                        validationGroup={vGroup}
                                        field="COVERAGE_LEARN_MORE_DESCRIPTION"
                                        onTranslationMethodsReady={setStorefrontScopeMethods}
                                    />
                                </TranslationScope>
                            </div>
                        </ControlGroupWithPreview>

                        <ControlGroupWithPreview
                            hasTopBorder={false}
                            gridStyle={{childrenWidth: {width: 1}, previewWidth: {width: 1}}}
                            preview={<img src={coverage_portal} alt=""/>}
                        >
                            <div className="edit-translation-field">
                                <FontBody1 color={FONT_COLOR.PRIMARY2}>{getTranslation('EDIT_PORTAL_TITLE_FIELD_TITLE', scope)}</FontBody1>
                                <FontBody2 color={FONT_COLOR.NEUTRAL3}>{getTranslation('EDIT_PORTAL_TITLE_FIELD_DESCRIPTION', scope)}</FontBody2>
                                <TranslationInput required validationGroup={vGroup} translationKey={"YOUR_RETURN_IS_FREE"} />
                            </div>

                            <div className="edit-translation-field">
                                <FontBody1 color={FONT_COLOR.PRIMARY2}>{getTranslation('EDIT_PORTAL_EXPLANATION_FIELD_TITLE', scope)}</FontBody1>
                                <FontBody2 color={FONT_COLOR.NEUTRAL3}>{getTranslation('EDIT_PORTAL_EXPLANATION_FIELD_DESCRIPTION', scope)}</FontBody2>
                                <TranslationTextarea required validationGroup={vGroup} translationKey={"YOUR_RETURN_IS_FREE_DESCRIPTION"} />
                            </div>
                        </ControlGroupWithPreview>
                    </Card>
                </ListLayout>
            </EditorPageLayout>
        </>
    )
});

interface IStorefrontScope {
    hasChanges: ITranslationScopeContext['hasChanges'],
    save: ITranslationScopeContext['save'],
}

interface ICoverageContentFieldProps {
    field: string;
    required?: boolean;
    validationGroup?: IValidationGroup;
    onTranslationMethodsReady: (scope: IStorefrontScope) => void;
}

const CoverageContentField: React.FC<ICoverageContentFieldProps> = observer(({
    onTranslationMethodsReady, field, required = false, validationGroup,
}) => {
    const { save, hasChanges } = useTranslation();
    useEffect(() => {
        onTranslationMethodsReady({ hasChanges, save });
    }, [onTranslationMethodsReady, save, hasChanges ]);

    return <TranslationInput translationKey={field} required={required} validationGroup={validationGroup} />;
});


export default CoverageSettingsEnabledPage;
